import { Box, Typography, TextField, UserFlag, Avatar, Button, UserSocialMediaEditor, ImageUpload } from "src/components";
import CountrySelector from "../account-management/country-selector"; 
import { useState, useLayoutEffect } from "react";
import { useIsMobile } from "src/hooks";
import countries from "world-countries";

export default function UserDetailsScreen({ form, setForm, scrollRef }) {
    const [showCountryPicker, setShowCountryPicker] = useState(false);
    const isMobile = useIsMobile();
    const subtitleSize = isMobile ? "subtitle1" : "body2";

    useLayoutEffect(() => {
        if (scrollRef?.current && showCountryPicker) {
            scrollRef.current.scrollTo({ top: 0, behavior: 'auto' });
        }
    }, [showCountryPicker, scrollRef]);

    const selectedCountry = countries.find(
        (c) => c.cca2 === form.countryIsoAlpha2
    );

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setForm({
                ...form,
                profileImage: file,
                previewImage: URL.createObjectURL(file),
            });
        }
    };

    const handleVehicleImageChange = (file) => {
        if (file) {
            setForm({
                ...form,
                vehicleImage: file,
                previewVehicleImage: URL.createObjectURL(file),
            });
        }
    };

    const handleInputChange = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    return (
        <Box>
        {showCountryPicker ? (
            <CountrySelector
                onClose={() => setShowCountryPicker(false)}
                onSelect={(code) => {
                    setForm((prev) => ({ ...prev, countryIsoAlpha2: code }));
                    setShowCountryPicker(false);
                }}
                selected={form.countryIsoAlpha2}
            />
        ) : (
            <Box display="flex" flexDirection="column" alignItems="center" width="100%">
                <Box sx={{mt: 3}}>
                    {/* Name */}
                    <Typography variant={subtitleSize} fontWeight={600} sx={{ mb: 0.5 }}>
                        Name
                    </Typography>
                    <TextField
                        fullWidth
                        size={isMobile ? "normal" : "small"}
                        value={form.name}
                        onChange={handleInputChange("name")}
                        placeholder="Enter your name"
                        margin="dense"
                    />

                    {/* Bio */}
                    <Typography variant={subtitleSize} fontWeight={600} sx={{ mt: 2, mb: 0.5 }}>
                        Bio
                    </Typography>
                    <TextField
                        fullWidth
                        size={isMobile ? "normal" : "small"}
                        value={form.bio}
                        onChange={handleInputChange("bio")}
                        placeholder="Tell us about yourself"
                        multiline
                        rows={5}
                        margin="dense"
                        inputProps={{ maxLength: 250 }}
                    />

                    {/* Nationality */}
                    <Typography variant={subtitleSize} fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                        Nationality
                    </Typography>
                    <Button
                        variant="outlined"
                        color="inherit"
                        fullWidth
                        onClick={() => {
                            setShowCountryPicker(true);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        sx={(theme) => ({
                            mb: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: "flex-start",
                            height: isMobile ? 55 : 35,
                            gap: 1,
                            borderColor: theme.palette.grey[400], 
                            '&:hover': {
                                borderColor: theme.palette.text.primary,
                                backgroundColor: 'transparent',
                            },
                        })}
                        >
                            {form.countryIsoAlpha2 ? <UserFlag countryCode={form.countryIsoAlpha2} /> : null}
                            <Typography
                                variant={subtitleSize}
                                fontWeight={600}
                                sx={{ lineHeight: 1, display: 'inline-block' }}
                                >
                                {selectedCountry ? selectedCountry.name.common : "Select a country"}
                            </Typography>
                    </Button>

                    {/* Avatar Upload */}
                    <Typography variant={subtitleSize} fontWeight={600} mb={1}>
                        Profile Photo
                    </Typography>
                    <Box position="relative" sx={{ cursor: "pointer", mb: 2, mt: 2 }}>
                        {/*<Avatar
                            src={form.previewImage || ""}
                            alt="Profile"
                            sx={{
                                width: 80,
                                height: 80,
                                border: "3px solid #ccc",
                                boxShadow: 2,
                            }}
                        />

                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            width="100%"
                            height="100%"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                bgcolor: "rgba(0,0,0,0.4)",
                                color: "#fff",
                                opacity: 0,
                                transition: "opacity 0.2s",
                                borderRadius: "50%",
                                "&:hover": { opacity: 1 }
                            }}
                        >
                            Change
                        </Box>
                        <input
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/heic,image/heif"
                            id="avatar-upload"
                            style={{ display: "none" }}
                            onChange={handleImageChange}
                        />*/}
                        <ImageUpload
                            previewUrl={form.previewImage}
                            onUpload={(file, preview) => setForm({ ...form, profileImage: file, previewImage: preview })}
                            circular={true}
                            size={80}    
                            aspect={1}
                            showClear={false}
                            />
                    </Box>

                    <Typography variant={subtitleSize} mb={1}>
                        You and Your Rig
                    </Typography>
                    <Box mb={3}>
                        {/*<ImageUpload
                            previewUrl={form.previewVehicleImage}
                            onUpload={handleVehicleImageChange}
                            showClear={false} />*/}
                        <ImageUpload
                            previewUrl={form.previewVehicleImage}
                            onUpload={handleVehicleImageChange}
                            aspect={1}
                            showClear={false}
                            />
                    </Box>

                    {/* Social media editor */}
                    <Box sx={{pb: 3}}>
                        <UserSocialMediaEditor
                            instagramHandle={form.instagramHandle}
                            socialMediaLinks={form.socialMediaLinks}
                            size={isMobile ? "large" : "small"}
                            onChangeInstagram={(newIg) => {
                                setForm({ ...form, instagramHandle: newIg });
                            }}
                            onChangeLinks={(newLinks) => {
                                setForm({ ...form, socialMediaLinks: newLinks });
                            }}
                        />
                    </Box>
                </Box>

            </Box>
        )}
        </Box>
    );
}
